import React, { useState } from "react";
import { Button, Modal, Space, message, Spin } from "antd";
import { FaFileDownload, FaEye, FaCloudUploadAlt, FaSave } from "react-icons/fa";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import moment from "moment";
import formTemplate from "../../../../assets/ESI_FORM.pdf";
import { useDispatch, useSelector } from 'react-redux';
import { uploadPdfToS3, getESIInfo } from '../../../redux/slice/hrFormSlice';
import photographImage from "../../../../assets/photograph.jpg";


const PdfESI = ({ formData, jwt_payload_group, jwtBearer }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const dispatch = useDispatch();

  const drawCheckmark = (page, x, y) => {
    page.drawLine({
      start: { x: x - 3, y: y + 1 },
      end: { x: x, y: y - 2 },
      thickness: 1.2,
      color: rgb(0, 0, 0),
    });
    page.drawLine({
      start: { x: x, y: y - 2 },
      end: { x: x + 5, y: y + 3 },
      thickness: 1.2,
      color: rgb(0, 0, 0),
    });
  };

  const CHECKMARK_POSITIONS = {
    maritalStatus: {
      'M': { x: 290, y: 681 },  // Married
      'U': { x: 305, y: 681 },  // Unmarried
      'W': { x: 324, y: 681 }   // Widow/Widower
    },
    gender: {
      'M': { x: 290, y: 667 },  // Male
      'F': { x: 305, y: 667 }   // Female
    }
  };

  const fillPdfForm = async () => {
    try {
      // Fetch the existing PDF template
      const existingPdfBytes = await fetch(formTemplate).then(res => res.arrayBuffer());

      // Load the PDF document
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];

      // Load fonts
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Set common text properties
      const textOptions = {
        size: 8,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      };

// ======== PHOTOGRAPH EMBEDDING ========
try {
  const imageUrl = formData?.photograph;
  
  // Skip if no image URL
  if (!imageUrl) {
    console.warn("No photograph URL provided");
    return;
  }

 const response = await fetch(imageUrl);
  if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
  
  const imageBytes = await response.arrayBuffer();
  
  const byteArray = new Uint8Array(imageBytes);
  let imageEmbed;
  
  if (byteArray[0] === 0xFF && byteArray[1] === 0xD8) {
    imageEmbed = await pdfDoc.embedJpg(imageBytes);
  } 
  else if (byteArray[0] === 0x89 && byteArray[1] === 0x50) {
    imageEmbed = await pdfDoc.embedPng(imageBytes);
  }
  else {
    console.warn("Unknown image format, using default image");
    const defaultImageBytes = await fetch(photographImage).then(res => res.arrayBuffer());
    imageEmbed = await pdfDoc.embedJpg(defaultImageBytes);
  }

  firstPage.drawImage(imageEmbed, {
    x: 410,
    y: 103,
    width: 73,
    height: 73,
  });
} catch (error) {
  console.error("Error embedding photograph:", error);
try {
    const defaultImageBytes = await fetch(photographImage).then(res => res.arrayBuffer());
    const defaultImageEmbed = await pdfDoc.embedJpg(defaultImageBytes);
    firstPage.drawImage(defaultImageEmbed, {
      x: 410,
      y: 103,
      width: 73,
      height: 73,
    });
  } catch (fallbackError) {
    console.error("Failed to embed default image:", fallbackError);
  }
}

  

      // SECTION A: Insured Person's Particulars
      firstPage.drawText(formData.section1?.insuranceNo || '', {
        x: 165,
        y: 740,
        ...textOptions
      });

      firstPage.drawText(formData.section1?.name?.toUpperCase() || '', {
        x: 165,
        y: 720,
        ...textOptions
      });

      firstPage.drawText(formData.section1?.fatherHusbandName?.toUpperCase() || '', {
        x: 165,
        y: 697,
        ...textOptions
      });

      if (formData.section1?.dob) {
        const dob = moment(formData.section1.dob);
        firstPage.drawText(dob.format('DD'), { x: 160, y: 668, ...textOptions });
        firstPage.drawText(dob.format('MM'), { x: 180, y: 668, ...textOptions });
        firstPage.drawText(dob.format('YY'), { x: 200, y: 668, ...textOptions });
      }

      if (formData.section1?.maritalStatus) {
        const maritalStatus = formData.section1.maritalStatus.toUpperCase();
        const position = CHECKMARK_POSITIONS.maritalStatus[maritalStatus];
        if (position) drawCheckmark(firstPage, position.x + 2, position.y - 2);
      }

      if (formData.section1?.gender) {
        const gender = formData.section1.gender.toUpperCase();
        const position = CHECKMARK_POSITIONS.gender[gender];
        if (position) drawCheckmark(firstPage, position.x + 2, position.y - 2);
      }

      const presentAddress = formData.section1?.presentAddress?.toUpperCase() || '';
      firstPage.drawText(presentAddress, {
        x: 95,
        y: 638,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0),
        maxWidth: 120,
        lineHeight: 15
      });

      const pinWithGaps = formData.section1?.presentPin
        ? formData.section1.presentPin.split('').join('     ')
        : '';
      firstPage.drawText(pinWithGaps, {
        x: 115,
        y: 579,
        ...textOptions
      });

      firstPage.drawText(formData.section1?.email || '', {
        x: 85,
        y: 557,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0)
      });

      const permanentAddress = formData.section1?.permanentAddress?.toUpperCase() || '';
      firstPage.drawText(permanentAddress, {
        x: 232,
        y: 638,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0),
        maxWidth: 120,
        lineHeight: 15
      });

      const permanentPinWithGaps = formData.section1?.permanentPin
        ? formData.section1.permanentPin.split('').join('     ')
        : '';
      firstPage.drawText(permanentPinWithGaps, {
        x: 250,
        y: 579,
        ...textOptions
      });

      firstPage.drawText(formData.section1?.permanentEmail || '', {
        x: 222,
        y: 557,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0)
      });

      // SECTION B: Employer's Particulars
      firstPage.drawText(formData.section2?.employerCode || '', {
        x: 265,
        y: 783,
        ...textOptions
      });

      firstPage.drawText(formData.section2?.previousEmployerCode || '', {
        x: 435,
        y: 578,
        ...textOptions
      });

      const dateStr = moment(formData.section2.appointmentDate).format('DD-MM-YYYY');
      const formattedDate = dateStr.replace(/-/g, '        ');
      firstPage.drawText(formattedDate, {
        x: 445,
        y: 722,
        ...textOptions
      });

      firstPage.drawText(formData.section2?.employerName || '', {
        x: 365,
        y: 680,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });

      firstPage.drawText(formData.section2?.employerAddress || '', {
        x: 365,
        y: 665,
        size: 7,
        maxWidth: 160,
        lineHeight: 10,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });

      firstPage.drawText(formData.section2?.previousInsNo || '', {
        x: 435,
        y: 593,
        size: 8,
        maxWidth: 180,
        lineHeight: 9,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });

      firstPage.drawText(formData.section1?.nameAddressEmployer || '', {
        x: 360,
        y: 556,
        size: 6,
        maxWidth: 150,
        lineHeight: 9,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });

      // SECTION C: Nominee Details
      firstPage.drawText(formData.section3?.nomineeName?.toUpperCase() || '', {
        x: 90,
        y: 488,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0)
      });

      firstPage.drawText(formData.section3?.nomineeRelationship?.toUpperCase() || '', {
        x: 255,
        y: 488,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0)
      });

      firstPage.drawText(formData.section3?.nomineeAddress?.toUpperCase() || '', {
        x: 350,
        y: 493,
        size: 7,
        font: helveticaFont,
        color: rgb(0, 0, 0),
        maxWidth: 170,
        lineHeight: 10
      });

      // SECTION D: Family Particulars
      formData.section4?.familyData?.forEach((member, index) => {
        if (index >= 7) return;
        const yPos = 330 - (index * 16);

        firstPage.drawText(member.name?.toUpperCase() || '', {
          x: 100,
          y: yPos,
          size: 6,
          font: helveticaFont,
          color: rgb(0, 0, 0)
        });

        firstPage.drawText(member.dob ? moment(member.dob).format('DD-MM-YYYY') : '', {
          x: 240,
          y: yPos,
          size: 6,
          font: helveticaFont,
          color: rgb(0, 0, 0),
          maxWidth: 80
        });

        const relationshipText = member.relationship === 'other'
          ? member.customRelationship?.toUpperCase() || ''
          : member.relationship?.toUpperCase() || '';

        firstPage.drawText(relationshipText, {
          x: 297,
          y: yPos,
          size: 6,
          font: helveticaFont,
          color: rgb(0, 0, 0),
          maxWidth: 80
        });


        firstPage.drawText(member.residing?.toUpperCase() || '', {
          x: 385,
          y: yPos,
          size: 6,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });

        firstPage.drawText(member.town?.toUpperCase() || '', {
          x: 440,
          y: yPos,
          size: 5,
          font: helveticaFont,
          color: rgb(0, 0, 0),
          maxWidth: 80
        });

        firstPage.drawText(member.state?.toUpperCase() || '', {
          x: 482,
          y: yPos,
          size: 5,
          font: helveticaFont,
          color: rgb(0, 0, 0),
          maxWidth: 80
        });
      });

      // SECTION E: Temporary Identity Card
      firstPage.drawText(formData.section1?.name?.toUpperCase() || '', {
        x: 154,
        y: 176,
        size: 8,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });

      if (formData.section5?.entryDate) {
        const entryDate = moment(formData.section5.entryDate);
        firstPage.drawText(entryDate.format('DD/MM/YYYY'), {
          x: 290,
          y: 156,
          size: 8,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
      }

      firstPage.drawText(formData.section5?.cardInsNo || '', {
        x: 154,
        y: 156,
        size: 8,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      });

      const fatherHusbandName = formData.section1?.fatherHusbandName?.toUpperCase() || '';
      firstPage.drawText(fatherHusbandName, {
        x: 155,
        y: 144,
        size: 5,
        font: helveticaFont,
        color: rgb(0, 0, 0),
        maxWidth: 60,
        lineHeight: 8
      });

      if (formData.section1?.dob) {
        const dob = moment(formData.section1.dob);
        firstPage.drawText(dob.format('DD/MM/YYYY'), {
          x: 290,
          y: 140,
          size: 8,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
      }

      if (formData.section5?.cardValidity) {
        const validity = moment(formData.section5.cardValidity);
        firstPage.drawText(validity.format('DD/MM/YYYY'), {
          x: 114,
          y: 45,
          size: 8,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
      }

      if (formData.section5?.cardDated) {
        const dated = moment(formData.section5.cardDated);
        firstPage.drawText(dated.format('DD/MM/YYYY'), {
          x: 114,
          y: 32,
          size: 8,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
      }

      // Save the filled PDF
      const pdfBytes = await pdfDoc.save();
      return new Blob([pdfBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('Error filling PDF form:', error);
      throw error;
    }
  };

  const handlePreview = async () => {
    try {
      setLoadingPreview(true);
      const blob = await fillPdfForm();
      setPdfUrl(URL.createObjectURL(blob));
      setPreviewVisible(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      message.error('Failed to generate PDF preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownload = async () => {
    try {
      setLoadingDownload(true);
      const blob = await fillPdfForm();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `ESI-Form-${formData.user_id || 'unknown'}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }, 100);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      message.error('Failed to download PDF');
    } finally {
      setLoadingDownload(false);
    }
  };

  const handleUploadToS3 = async () => {
    try {
      message.loading({ content: 'Saving form data...', key: 'upload', duration: 0 });
      setLoadingUpload(true);

      const pdfBlob = await fillPdfForm();
      const pdfFile = new File([pdfBlob], `ESI_Form_${formData.user_id}.pdf`, {
        type: 'application/pdf'
      });

      const resultAction = await dispatch(uploadPdfToS3({
        user_id: formData.user_id,
        file: pdfFile,
        file_type: 'esi',
        jwtBearer
      }));

      if (uploadPdfToS3.fulfilled.match(resultAction)) {
        message.success({ content: 'Form saved successfully!', key: 'upload' });
      } else {
        throw new Error(resultAction.error.message || resultAction.payload);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      message.error({
        content: error.message || 'Failed to save form',
        key: 'upload'
      });
    } finally {
      setLoadingUpload(false);
    }
  };

  return (
    <>
      <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {/* <Button
          type="default"
          onClick={handlePreview}
          icon={loadingPreview ? <Spin /> : <FaEye />}
          disabled={loadingPreview}
        >
          {loadingPreview ? 'Generating...' : 'Preview'}
        </Button> */}
        <Button
          type="primary"
          onClick={handleDownload}
          icon={loadingDownload ? <Spin /> : <FaFileDownload />}
          disabled={loadingDownload || loadingPreview}
        >
          {loadingDownload ? 'Preparing...' : 'Download'}
        </Button>
        {(formData?.is_section1_completed === true &&
          formData?.is_section3_completed === true &&
          formData?.is_section4_completed === true &&
          formData?.is_section5_completed === true) && (
            <Button
              type="primary"
              onClick={handleUploadToS3}
              icon={loadingUpload ? <Spin /> : <FaSave />}
              disabled={loadingUpload || loadingDownload}
              style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
            >
              {loadingUpload ? 'Saving...' : 'Save'}
            </Button>
          )}
      </Space>

      <Modal
        title="ESI Declaration Form Preview"
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        }}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        destroyOnClose
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="800px"
            style={{ border: 'none' }}
            title="ESI Form PDF Preview"
          />
        )}
      </Modal>
    </>
  );
};

export default PdfESI;